import type {KeyboardEvent, MouseEvent, ReactElement} from 'react';
import {Icon} from '@Components/icon-v2';
import {IconSize} from '@Components/icon-v2/icon.types';
import {Text, TextSize} from '@Components/text';
import {noop} from '@Utils/general.util';
import type {ChipProps} from './chip.types';
import {ChipSize, ChipVariant, REMOVE_CHIP_KEYS} from './chip.types';
import styles from './chip.module.scss';

export function Chip({
  label,
  onClick = noop,
  isSelected = false,
  size = ChipSize.MD,
  variant = ChipVariant.DEFAULT,
  icon = '',
  isRemovable = false,
  onRemove,
  isDisabled = false,
  className = '',
  'data-testid': dataTestId,
}: ChipProps): ReactElement {
  const isLarge = size === ChipSize.LG;
  const canRemoveFromKeyboard = Boolean(onRemove) && isSelected && isRemovable;
  const glyphSize = isLarge ? IconSize.SIZE_ICON_20 : IconSize.SIZE_ICON_16;

  const renderIcon = (): ReactElement | null => {
    if (!icon) {
      return null;
    }
    return <Icon icon={icon} size={glyphSize} className={`${styles.content} ${styles.glyph}`} ariaHidden />;
  };

  const removeChip = (event: MouseEvent): void => {
    if (!onRemove) {
      return;
    }

    event.stopPropagation();
    onRemove();
  };

  const removeChipFromKeyboard = (event: KeyboardEvent): void => {
    if (!onRemove || !canRemoveFromKeyboard || !REMOVE_CHIP_KEYS.includes(event.key)) {
      return;
    }

    event.preventDefault();
    onRemove();
  };

  const getRemoveKeyShortcuts = (): string | undefined => {
    if (!canRemoveFromKeyboard) {
      return undefined;
    }

    return REMOVE_CHIP_KEYS.join(' ');
  };

  const renderRemoveGlyph = (): ReactElement | null => {
    if (!isSelected || !isRemovable) {
      return null;
    }
    return <Icon icon="icon-close" size={glyphSize} className={`${styles.content} ${styles.glyph}`} ariaHidden onClick={removeChip} />;
  };

  return (
    <button
      type="button"
      className={`${styles.chip} ${isLarge ? styles.large : styles.medium} ${variant === ChipVariant.AI ? styles.ai : ''} ${className}`}
      aria-pressed={isSelected}
      disabled={isDisabled}
      data-testid={dataTestId}
      onClick={onClick}
      onKeyDown={removeChipFromKeyboard}
      aria-keyshortcuts={getRemoveKeyShortcuts()}
    >
      {renderIcon()}
      <Text val={label} size={isLarge ? TextSize.MD : TextSize.SM} bold className={`${styles.content} ${styles.label}`} />
      {renderRemoveGlyph()}
    </button>
  );
}
